/**
 * Core API Response Types
 * Standard response format for all API endpoints
 */

/**
 * Generic API response wrapper
 * @template T - Response data type
 * @property {boolean} success - Whether request was successful
 * @property {string} message - Response message
 * @property {T} [data] - Response data
 * @property {string[]} [errors] - Error messages if any
 * @property {string} [cid] - Correlation ID for tracking
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
  cid?: string;
}

/**
 * Pagination options for list endpoints
 * @property {number} page - Page number (1-based)
 * @property {number} pageSize - Items per page
 * @property {string} [sortBy] - Field to sort by
 * @property {'asc' | 'desc'} [sortDirection] - Sort direction
 */
export interface PaginationOptions {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Generic filter options
 */
export interface FilterOptions {
  [key: string]: any;
}

/**
 * Table column configuration
 * @property {string} field - Field name
 * @property {string} header - Column header label
 * @property {boolean} sortable - Whether column is sortable
 * @property {boolean} filterable - Whether column is filterable
 * @property {boolean} [editable] - Whether column is editable
 * @property {'text' | 'number' | 'select' | 'date'} [type] - Field type
 * @property {Array<{value: any, label: string}>} [options] - Select options
 */
export interface TableColumn {
  field: string;
  header: string;
  sortable: boolean;
  filterable: boolean;
  editable?: boolean;
  type?: 'text' | 'number' | 'select' | 'date';
  options?: { value: any; label: string }[];
}

/**
 * Form field configuration
 * @property {string} field - Field name
 * @property {string} label - Field label
 * @property {'text' | 'number' | 'email' | 'password' | 'select'} type - Input type
 * @property {string} [placeholder] - Placeholder text
 * @property {boolean} [required] - Whether field is required
 * @property {Array<{value: any, label: string}>} [options] - Select options
 */
export interface FormFieldConfig {
  field: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'password' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: { value: any; label: string }[];
}

/**
 * User role type
 * Administrator: Full access to all features
 * Manager: Can manage pricing, stations, and uploads
 * Marketer: Can upload and view pricing
 * User: Read-only access
 */
export type UserRole = 'Administrator' | 'Manager' | 'Marketer' | 'User';

/**
 * Authenticated user information
 * @property {number} id - User ID
 * @property {string} azure_object_id - Azure AD object ID
 * @property {string} email - User email
 * @property {string} display_name - Display name
 * @property {UserRole} role - User role
 * @property {boolean} is_active - Whether user is active
 */
export interface User {
  id: number;
  azure_object_id: string;
  email: string;
  display_name: string;
  role: UserRole;
  is_active: boolean;
}

/**
 * Azure AD claims from token
 * @property {string} oid - Object ID
 * @property {string} upn - User principal name
 * @property {string} name - User name
 * @property {number} exp - Token expiration time
 */
export interface AzureClaims {
  oid: string;
  upn: string;
  name: string;
  exp: number;
}

/**
 * Authentication response from backend
 * @property {string} message - Response message
 * @property {User} [user] - Authenticated user
 * @property {AzureClaims} [azure_claims] - Azure claims
 * @property {string} [error] - Error message if any
 * @property {string} cid - Correlation ID
 * @property {boolean} [development_mode] - Whether in dev mode
 * @property {Object} [azure_user] - Azure user info
 */
export interface AuthResponse {
  message: string;
  user?: User;
  azure_claims?: AzureClaims;
  error?: string;
  cid: string;
  version?: string;
  development_mode?: boolean;
  azure_user?: {
    oid: string;
    upn: string;
    name: string;
  };
}

/**
 * Role-based permissions
 * @property {boolean} canViewAll - Can view all data
 * @property {boolean} canEditAll - Can edit all data
 * @property {boolean} canManageUsers - Can manage users
 * @property {boolean} canUpload - Can upload files
 * @property {boolean} canDelete - Can delete data
 * @property {boolean} canEditPricing - Can edit pricing
 * @property {boolean} canManageStations - Can manage stations
 * @property {boolean} canViewConnections - Can view station connections
 * @property {boolean} canEdit - Can edit data
 */
export interface RolePermissions {
  canViewAll: boolean;
  canEditAll: boolean;
  canManageUsers: boolean;
  canUpload: boolean;
  canDelete: boolean;
  canEditPricing: boolean;
  canManageStations: boolean;
  canViewConnections: boolean;
  canEdit: boolean;
}

/**
 * Role permissions mapping
 * Defines permissions for each user role
 */
export const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  Administrator: {
    canViewAll: true,
    canEditAll: true,
    canManageUsers: true,
    canUpload: true,
    canDelete: true,
    canEditPricing: true,
    canManageStations: true,
    canViewConnections: true,
    canEdit: true
  },
  Manager: {
    canViewAll: true,
    canEditAll: false,
    canManageUsers: false,
    canUpload: true,
    canDelete: false,
    canEditPricing: true,
    canManageStations: true,
    canViewConnections: true,
    canEdit: true
  },
  Marketer: {
    canViewAll: true,
    canEditAll: false,
    canManageUsers: false,
    canUpload: true,
    canDelete: false,
    canEditPricing: true,
    canManageStations: true,
    canViewConnections: true,
    canEdit: true
  },
  User: {
    canViewAll: true,
    canEditAll: false,
    canManageUsers: false,
    canUpload: false,
    canDelete: false,
    canEditPricing: false,
    canManageStations: false,
    canViewConnections: true,
    canEdit: false
  }
};

/**
 * Core station information
 * @property {number} id - Station ID
 * @property {string} unicode - Station unicode identifier
 * @property {string} province - Province name
 * @property {string} brand - Brand name
 * @property {string} address - Station address
 * @property {number} station_number - Station number
 */
export interface Station {
  id: number;
  unicode: string;
  province: string;
  brand: string;
  address: string;
  station_number: number;
}

/**
 * Detailed station profile information
 * Contains all station details including contact info and coordinates
 */
export interface StationProfile {
  id: number;
  unicode: string;
  regional_office: string;
  province: string;
  division: string;
  address: string;
  address_note: string;
  status: string;
  start_date: string;
  brand: string;
  station_number: number;
  brand_date: string;
  status_date: string;
  photo_url: string;
  latitude: number;
  longitude: number;
  additional_code: string;
  station_type: string;
  location_type: string;
  shop_area: number;
  shop_type: string;
  email: string;
  phone1: string;
  phone2: string;
  manager_name: string;
  manager_email: string;
  manager_phone: string;
  supervisor_name: string;
  supervisor_email: string;
  supervisor_phone: string;
  created_at: string;
  updated_at: string;
}

/**
 * Stations list response
 * @property {Object} data - Response data
 * @property {Station[]} data.stations - Stations list
 * @property {number} data.total - Total count
 * @property {number} data.page - Current page
 * @property {number} data.limit - Items per page
 * @property {number} data.totalPages - Total pages
 * @property {string} cid - Correlation ID
 */
export interface StationsResponse {
  data: {
    stations: Station[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  cid: string;
}

/**
 * Filter options response
 * @property {Object} data - Response data
 * @property {string[]} data.regional_offices - Available regional offices
 * @property {string[]} data.provinces - Available provinces
 * @property {string[]} data.brands - Available brands
 * @property {string[]} data.statuses - Available statuses
 * @property {string} cid - Correlation ID
 */
export interface FiltersResponse {
  data: {
    regional_offices: string[];
    provinces: string[];
    brands: string[];
    statuses: string[];
  };
  cid: string;
}

/**
 * Station filter parameters
 * @property {string} [regional_office] - Regional office filter
 * @property {string} [province] - Province filter
 * @property {string} [brand] - Brand filter
 * @property {string} [status] - Status filter
 * @property {string} [search] - Search text
 * @property {string} [sortBy] - Sort field
 * @property {'ASC' | 'DESC'} [sortOrder] - Sort direction
 * @property {number} [page] - Page number
 * @property {number} [limit] - Items per page
 */
export interface StationFilters {
  regional_office?: string;
  province?: string;
  brand?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

/**
 * Brand status enum
 */
export enum BrandStatus {
  ACTIVE = 'active',
  CLOSED = 'closed'
}

/**
 * Brand information
 * @property {number} id - Brand ID
 * @property {string} name - Brand name
 * @property {number} order_number - Display order
 * @property {number} azk_count - Total AZK count
 * @property {number} active_azk_count - Active AZK count
 * @property {BrandStatus} status - Brand status
 * @property {boolean} [_changed] - UI state for tracking changes
 * @property {string} [cid] - Correlation ID
 */
export interface Brand {
  id: number;
  name: string;
  order_number: number;
  azk_count: number;
  active_azk_count: number;
  status: BrandStatus;
  _changed?: boolean;
  cid?: string;
}

/**
 * Brand response
 * @property {string} [message] - Response message
 * @property {string} cid - Correlation ID
 */
export interface BrandResponse {
  message?: string;
  cid: string;
}

/**
 * Brands response
 * @property {string} [message] - Response message
 * @property {string} cid - Correlation ID
 */
export interface BrandsResponse {
  message?: string;
  cid: string;
}

/**
 * Brand form data
 * @property {string} brandId - Brand ID
 * @property {string} brandName - Brand name
 * @property {string | number} order - Display order
 */
export interface BrandFormData {
  brandId: string;
  brandName: string;
  order: string | number;
}

/**
 * Brand status option for select
 * @property {BrandStatus} value - Status value
 * @property {string} label - Display label
 */
export interface BrandStatusOption {
  value: BrandStatus;
  label: string;
}

/**
 * Gets localized label for brand status
 * @param {BrandStatus} status - Brand status
 * @returns {string} Localized label
 */
export const getBrandStatusLabel = (status: BrandStatus): string => {
  switch (status) {
    case BrandStatus.ACTIVE:
      return 'Діючий';
    case BrandStatus.CLOSED:
      return 'Закритий';
    default:
      return status;
  }
};

/**
 * Brand status options for select dropdowns
 */
export const BRAND_STATUS_OPTIONS: BrandStatusOption[] = [
  { value: BrandStatus.ACTIVE, label: 'Діючий' },
  { value: BrandStatus.CLOSED, label: 'Закритий' }
];

/**
 * Brand table column configuration
 */
export interface BrandTableColumn {
  field: string;
  header: string;
  sortable: boolean;
  filterable: boolean;
  editable?: boolean;
  type?: string;
  options?: BrandStatusOption[];
}

/**
 * Upload type enum
 * OKKO_PRICES: OKKO station prices
 * COMPETITOR_PRICES: Competitor station prices
 * DISCOUNTS: Discount data
 * COMPETITORS_DATA: Competitor station connections
 */
export enum UploadType {
  OKKO_PRICES = 'azk-kgng',
  COMPETITOR_PRICES = 'azk-competitor',
  DISCOUNTS = 'discounts',
  COMPETITORS_DATA = 'connections'
}

/**
 * Upload type configuration
 * @property {UploadType} type - Upload type
 * @property {string} label - Display label
 * @property {boolean} hasOrderOptions - Whether upload has order options
 */
export interface UploadTypeConfig {
  type: UploadType;
  label: string;
  hasOrderOptions: boolean;
}

/**
 * Upload type configurations for all upload types
 */
export const UPLOAD_TYPE_CONFIGS: UploadTypeConfig[] = [
  {
    type: UploadType.OKKO_PRICES,
    label: 'АЗК ОККО',
    hasOrderOptions: true
  },
  {
    type: UploadType.COMPETITOR_PRICES,
    label: 'АЗК Конкурент',
    hasOrderOptions: false
  },
  {
    type: UploadType.DISCOUNTS,
    label: 'Знижки',
    hasOrderOptions: false
  },
  {
    type: UploadType.COMPETITORS_DATA,
    label: 'Завантаження конкурентів',
    hasOrderOptions: false
  }
];

export type StationsSortType = 'alphabetical' | 'bindings';

/**
 * Brand data structure
 */
export interface BasicBrand {
  /** Brand identifier */
  id: number;
  /** Brand name */
  name: string;
}

export type PriceActionFilterKey = 'regional_office' | 'province' | 'brand';

export interface PriceActionFilters {
  regional_office?: string | string[];
  province?: string | string[];
  brand?: string | string[];
  [key: string]: string | string[] | undefined;
}

/**
 * Active filter structure for display
 */
export interface PriceActionActiveFilter {
  /** Filter key identifier */
  key: PriceActionFilterKey;
  /** Display label for the filter */
  label: string;
  /** Filter value */
  value: string;
}
